import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, MailCheck, RefreshCcw, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import LegalFooter from '../components/LegalFooter';
import LanguageSwitcher from '../components/LanguageSwitcher';

/**
 * VerifyEmailPage — Phase 27.5
 *
 * Mandatory step between registration/login and accessing the app.
 * - Email is provided via ?email= query string (set by Register or Login redirect).
 * - 6-digit OTP input; on success the user is logged in (token stored)
 *   and routed to home.
 * - Resend button respects the backend's 60s rate-limit per user.
 */
const VerifyEmailPage = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, verifyEmailPublic, resendVerifyOtpPublic, logout } = useAuth();
  const { toast } = useToast();

  const params = new URLSearchParams(location.search);
  const initialEmail = params.get('email') || user?.email || '';

  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // If the user has already verified (e.g. opens this page after the
  // fact), bounce them home.
  useEffect(() => {
    if (user && user.email_verified) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Cooldown countdown for the Resend button.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const tid = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(tid);
  }, [cooldown]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const cleanOtp = otp.replace(/\s/g, '').trim();
    if (!email || !cleanOtp) {
      toast({ title: t('verify_email.missing_fields_toast'), variant: 'destructive' });
      return;
    }
    setVerifying(true);
    try {
      await verifyEmailPublic(email.toLowerCase().trim(), cleanOtp);
      toast({
        title: t('verify_email.success_toast_title'),
        description: t('verify_email.success_toast_desc'),
      });
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || t('verify_email.error_default');
      toast({
        title: t('verify_email.error_title'),
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setVerifying(false);
    }
  };

  const onResend = async () => {
    if (cooldown > 0) return;
    if (!email) {
      toast({ title: t('verify_email.missing_email_toast'), variant: 'destructive' });
      return;
    }
    setResending(true);
    try {
      await resendVerifyOtpPublic(email.toLowerCase().trim());
      toast({
        title: t('verify_email.resend_success_title'),
        description: t('verify_email.resend_success_desc'),
      });
      setCooldown(60);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // Parse rate-limit error: "rate_limited|Please wait Xs"
      const m = detail.match(/wait\s+(\d+)s/i);
      if (m) setCooldown(parseInt(m[1], 10) || 60);
      toast({
        title: t('verify_email.resend_error_title'),
        description: detail.split('|').pop() || t('verify_email.resend_error_default'),
        variant: 'destructive',
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10">
      <div className="mb-4 flex w-full max-w-md justify-end">
        <LanguageSwitcher variant="full" align="end" testId="verify-language-switcher" />
      </div>
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm" data-testid="verify-email-card">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15">
            <ShieldCheck className="h-7 w-7 text-amber-600" strokeWidth={1.8} />
          </div>
          <h1 className="text-center text-2xl font-bold text-foreground">{t('verify_email.title')}</h1>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            {t('verify_email.intro_line1')}
            <br />
            {t('verify_email.intro_line2')} <strong className="break-all">{email || '...'}</strong>.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('verify_email.email_label')}</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={!!initialEmail}
                className="h-10 bg-background"
                data-testid="verify-email-input"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground">{t('verify_email.otp_label')}</label>
              <Input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder={t('verify_email.otp_placeholder')}
                className="h-12 text-center text-2xl font-mono tracking-[0.4em] bg-background"
                data-testid="verify-email-otp-input"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{t('verify_email.otp_expiry_hint')}</p>
            </div>
            <Button
              type="submit"
              disabled={verifying || !otp}
              className="gold-gradient h-11 w-full text-base font-semibold"
              data-testid="verify-email-submit-btn"
            >
              {verifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MailCheck className="mr-2 h-4 w-4" />}
              {verifying ? t('verify_email.submitting') : t('verify_email.submit')}
            </Button>
          </form>

          <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
            <p className="text-xs text-muted-foreground">{t('verify_email.resend_prompt')}</p>
            <button
              type="button"
              onClick={onResend}
              disabled={resending || cooldown > 0}
              className="inline-flex items-center gap-1.5 text-xs font-semibold gold-text hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="verify-email-resend-btn"
            >
              {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              {cooldown > 0 ? t('verify_email.resend_cooldown', { seconds: cooldown }) : t('verify_email.resend')}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-3 text-xs">
          <Link to="/login" className="font-semibold text-muted-foreground hover:underline" data-testid="verify-email-back-login">
            {t('verify_email.back_to_login')}
          </Link>
          {user && (
            <>
              <span className="text-muted-foreground">·</span>
              <button
                type="button"
                onClick={() => { logout(); navigate('/login', { replace: true }); }}
                className="font-semibold text-muted-foreground hover:underline"
                data-testid="verify-email-logout-btn"
              >
                {t('verify_email.logout')}
              </button>
            </>
          )}
        </div>
      </div>
      <LegalFooter className="mt-8 max-w-md px-4" />
    </div>
  );
};

export default VerifyEmailPage;
